import React, { useEffect, useMemo, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Leaf, Droplets, Plane, Shirt, FileText, Check, ExternalLink, Info } from 'lucide-react';
import { useRef } from 'react';
import { useContent } from '../../lib/content';
import { api, pickLocale } from '../../lib/api';

/**
 * ImpactESG — V2 (on-prem realistic).
 *
 * No bombastic aggregated counters (we have no real client base yet — would
 * count as AI-washing / greenwashing per FTC 2025 enforcement guidance).
 *
 * What we show instead:
 *   1. The PER-ASSET baselines (audited public benchmarks · 100% real).
 *   2. An interactive calculator: brand types in their monthly asset volume,
 *      we estimate their potential savings — clearly labelled as ESTIMATION.
 *   3. Methodology block + partner alignment badges.
 *   4. CTA: request a real LCA audit on their current pipeline.
 */

const numFmt = (locale) => new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-GB', {
  maximumFractionDigits: 0,
});

const PARTNERS = [
  { name: 'Carbonfact',    role: 'fashion LCA partner',     status: 'in_progress', url: 'https://carbonfact.com' },
  { name: 'Higg Index',    role: 'Cascale 2025 alignment',  status: 'aligned',     url: 'https://cascale.org' },
  { name: 'GHG Protocol',  role: 'Scopes 1-3',              status: 'compliant',   url: 'https://ghgprotocol.org' },
  { name: 'ISO 14040',     role: 'LCA standard',            status: 'compliant',   url: 'https://www.iso.org/standard/37456.html' },
  { name: 'PEFCR',         role: 'Apparel & Footwear',      status: 'compliant',   url: 'https://environment.ec.europa.eu/news/new-eu-rules-measuring-environmental-impact-clothes-and-shoes-2025-06-25_en' },
];

function StatHighlight({ value, label, sub, Icon }) {
  return (
    <div className="border border-line p-5 md:p-6 bg-bg/40">
      <div className="flex items-center justify-between mb-4">
        <Icon size={16} className="text-acid" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">{sub}</span>
      </div>
      <div className="font-display text-3xl md:text-4xl text-ink leading-none tabular-nums">{value}</div>
      <div className="text-eyebrow text-ink-muted mt-3 leading-tight">{label}</div>
    </div>
  );
}

export default function ImpactESG() {
  const { content, locale } = useContent();
  const data = content.impact_esg || {};
  const [baselines, setBaselines] = useState([]);
  const [monthly, setMonthly] = useState(2000); // user-controlled estimator input

  useEffect(() => {
    let alive = true;
    api.get('/impact/baseline').then((r) => {
      if (alive && r?.data?.items) setBaselines(r.data.items);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const t = (k, fb = '') => pickLocale(data[k], locale, fb);
  const trad = baselines.find((x) => x.workflow === 'traditional_shoot');
  const ai = baselines.find((x) => x.workflow === 'ai_modaria_image');

  const fmt = useMemo(() => numFmt(locale), [locale]);

  // Estimator (single source of truth: per-asset baselines × user volume)
  const estimate = useMemo(() => {
    if (!trad || !ai || monthly <= 0) return null;
    return {
      co2:    Math.round((trad.co2_kg     - ai.co2_kg)     * monthly),
      water:  Math.round((trad.water_l    - ai.water_l)    * monthly),
      energy: Math.round((trad.energy_kwh - ai.energy_kwh) * monthly),
      samples: Math.round(trad.samples_units * monthly),
      travel:  Math.round(trad.travel_km     * monthly),
    };
  }, [trad, ai, monthly]);

  const compRows = [
    { key: 'co2_kg',        icon: Leaf,     label: { es: 'CO₂ por imagen',      en: 'CO₂ per image' },     unit: 'kg' },
    { key: 'water_l',       icon: Droplets, label: { es: 'Agua por imagen',     en: 'Water per image' },    unit: 'L' },
    { key: 'energy_kwh',    icon: Leaf,     label: { es: 'Energía por imagen', en: 'Energy per image' },   unit: 'kWh' },
    { key: 'samples_units', icon: Shirt,    label: { es: 'Muestras físicas',    en: 'Physical samples' },  unit: '' },
    { key: 'travel_km',     icon: Plane,    label: { es: 'Viajes amortizados', en: 'Amortised travel' },   unit: 'km' },
  ];

  return (
    <section
      id="impact"
      data-testid="impact-esg"
      className="relative section bg-bg overflow-hidden"
    >
      <div className="container-x">
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <Leaf size={14} className="text-acid" />
          <span className="text-eyebrow text-acid">{t('eyebrow', 'IMPACTO · LO QUE TU MARCA DEJARÍA DE GASTAR')}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <h2
              data-testid="impact-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'La moda es de las industrias más contaminantes. La nuestra no tiene por qué serlo.')}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed">
              {t('lead', 'MODARIA mide cada imagen y vídeo que produce tu sistema en casa y la compara contra la sesión tradicional equivalente, usando estándares públicos auditables. Aquí están los números por imagen y un estimador para tu volumen — sin cifras agregadas inventadas.')}
            </p>
          </div>
        </div>

        {/* Per-asset comparison (REAL) */}
        <div className="mb-16 md:mb-20" data-testid="impact-comparison">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="text-eyebrow text-ink-muted">{locale === 'es' ? 'POR IMAGEN · ESTÁNDARES AUDITADOS' : 'PER IMAGE · AUDITED BASELINES'}</div>
            <div className="text-eyebrow text-ink-dim flex items-center gap-1.5">
              <Info size={11} className="text-acid" />
              {locale === 'es' ? 'CIFRAS PÚBLICAS · MARGEN ±12%' : 'PUBLIC FIGURES · UNCERTAINTY ±12%'}
            </div>
          </div>
          <div className="overflow-x-auto -mx-5 md:mx-0">
            <table className="min-w-[760px] w-full border-t border-line">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left py-5 px-3 text-eyebrow text-ink-muted font-normal w-[42%]">
                    {locale === 'es' ? 'Métrica' : 'Metric'}
                  </th>
                  <th className="py-5 px-3 text-eyebrow text-ink-muted font-normal text-right">
                    {locale === 'es' ? 'Sesión tradicional' : 'Traditional shoot'}
                  </th>
                  <th className="py-5 px-3 text-acid font-mono uppercase tracking-[0.18em] text-[11px] text-right">
                    MODARIA
                  </th>
                  <th className="py-5 px-3 text-eyebrow text-acid font-normal text-right">
                    {locale === 'es' ? 'Reducción' : 'Reduction'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {compRows.map((row, i) => {
                  const Ic = row.icon;
                  const a = trad?.[row.key] ?? 0;
                  const b = ai?.[row.key] ?? 0;
                  const reduction = a > 0 ? Math.round((1 - b / a) * 100) : 0;
                  return (
                    <tr key={i} data-testid={`impact-row-${row.key}`} className="border-b border-line/60">
                      <td className="py-4 px-3 text-ink text-sm md:text-base">
                        <div className="flex items-center gap-2.5">
                          <Ic size={14} className="text-ink-muted" />
                          {pickLocale(row.label, locale)}
                        </div>
                      </td>
                      <td className="py-4 px-3 text-right text-ink-muted text-sm tabular-nums">
                        {a.toLocaleString(locale === 'es' ? 'es-ES' : 'en-GB', { maximumFractionDigits: 4 })} {row.unit}
                      </td>
                      <td className="py-4 px-3 text-right bg-acid/5 text-ink text-sm tabular-nums">
                        {b.toLocaleString(locale === 'es' ? 'es-ES' : 'en-GB', { maximumFractionDigits: 4 })} {row.unit}
                      </td>
                      <td className="py-4 px-3 text-right text-acid font-display text-lg md:text-xl tabular-nums">
                        −{reduction}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Estimator (clearly labelled ESTIMATION) */}
        <div className="border border-line bg-bg/40 p-6 md:p-10 mb-16 md:mb-20" data-testid="impact-estimator">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="text-eyebrow text-ink-muted">{locale === 'es' ? 'ESTIMADOR PARA TU MARCA' : 'ESTIMATOR FOR YOUR BRAND'}</div>
            <span className="text-eyebrow text-ink-dim border border-line px-2 py-1">
              {locale === 'es' ? 'ESTIMACIÓN · NO COMPROMISO' : 'ESTIMATE · NO COMMITMENT'}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <h3 className="font-display text-2xl md:text-3xl text-ink mb-4 leading-tight">
                {locale === 'es' ? '¿Cuántas imágenes producirás al mes?' : 'How many images will you produce per month?'}
              </h3>
              <p className="text-ink-muted text-sm leading-relaxed mb-5">
                {locale === 'es'
                  ? 'Mueve el control y verás una estimación del impacto mensual ahorrado basada en estándares auditados (no en clientes reales). El cálculo definitivo lo hace tu sistema en casa con tu mezcla real de imagen y vídeo.'
                  : 'Move the slider to see an estimated monthly footprint saved, based on audited baselines (not real clients). The definitive number is computed by your in-house system with your actual image/video mix.'}
              </p>

              <div className="font-display text-5xl md:text-6xl text-ink leading-none tabular-nums mb-2" data-testid="impact-estimator-volume">
                {fmt.format(monthly)}
              </div>
              <div className="text-eyebrow text-acid mb-5">{locale === 'es' ? 'IMÁGENES / MES' : 'IMAGES / MONTH'}</div>

              <input
                type="range"
                min="100"
                max="20000"
                step="100"
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value))}
                className="w-full accent-acid cursor-pointer"
                data-testid="impact-estimator-slider"
                aria-label={locale === 'es' ? 'Imágenes por mes' : 'Images per month'}
              />
              <div className="flex justify-between text-eyebrow text-ink-dim mt-2">
                <span>100</span>
                <span>20.000</span>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
              <StatHighlight
                value={estimate ? `~${fmt.format(estimate.co2)} kg` : '—'}
                label={locale === 'es' ? 'CO₂ ahorrado / mes' : 'CO₂ saved / month'}
                sub={locale === 'es' ? 'frente a sesión tradicional' : 'vs traditional shoot'}
                Icon={Leaf}
              />
              <StatHighlight
                value={estimate ? `~${fmt.format(estimate.water)} L` : '—'}
                label={locale === 'es' ? 'Agua ahorrada / mes' : 'Water saved / month'}
                sub={locale === 'es' ? 'muestras + textil' : 'samples + textile'}
                Icon={Droplets}
              />
              <StatHighlight
                value={estimate ? `~${fmt.format(estimate.samples)}` : '—'}
                label={locale === 'es' ? 'Muestras evitadas / mes' : 'Samples avoided / month'}
                sub={locale === 'es' ? 'menos residuo textil' : 'less textile waste'}
                Icon={Shirt}
              />
              <StatHighlight
                value={estimate ? `~${fmt.format(estimate.travel)} km` : '—'}
                label={locale === 'es' ? 'Viajes evitados / mes' : 'Travel avoided / month'}
                sub={locale === 'es' ? 'equipo + modelos' : 'crew + talent'}
                Icon={Plane}
              />
            </div>
          </div>
        </div>

        {/* Methodology + partners */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-7 border-t border-line pt-8">
            <div className="text-eyebrow text-ink-muted mb-4">{locale === 'es' ? 'METODOLOGÍA · TRANSPARENCIA' : 'METHODOLOGY · TRANSPARENCY'}</div>
            <h3 className="font-display text-2xl md:text-3xl text-ink mb-6 leading-tight">
              {t('method_title', 'Cifras prudentes, fuentes citadas, sin maquillaje verde.')}
            </h3>
            <ul className="space-y-3">
              {[
                { es: 'GHG Protocol Scopes 1-3', en: 'GHG Protocol Scopes 1-3' },
                { es: 'ISO 14040 — Life Cycle Assessment', en: 'ISO 14040 — Life Cycle Assessment' },
                { es: 'PEFCR Apparel & Footwear (rev. UE 2025)', en: 'PEFCR Apparel & Footwear (UE 2025 update)' },
                { es: 'Luccioni et al. 2024 — energía por imagen IA', en: 'Luccioni et al. 2024 — AI image energy' },
                { es: 'On-Model 2024 — coste carbono shoot tradicional', en: 'On-Model 2024 — traditional shoot carbon cost' },
                { es: 'Higg MSI · Cascale 2025', en: 'Higg MSI · Cascale 2025' },
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-ink text-sm">
                  <Check size={14} className="text-acid mt-1 shrink-0" />
                  <span>{pickLocale(s, locale)}</span>
                </li>
              ))}
            </ul>
            <p className="text-ink-dim text-xs font-mono uppercase tracking-[0.18em] mt-5">
              {t('method_uncertainty', 'MARGEN DE ERROR DECLARADO: ±12% · RED ELÉCTRICA UE 2026 ≈ 250 G CO₂/KWH')}
            </p>
          </div>

          <div className="lg:col-span-5 border-t border-line pt-8">
            <div className="text-eyebrow text-ink-muted mb-4">{locale === 'es' ? 'ESTÁNDARES Y ALINEACIÓN' : 'STANDARDS & ALIGNMENT'}</div>
            <div className="grid grid-cols-1 gap-2" data-testid="impact-partners">
              {PARTNERS.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between border border-line px-4 py-3 hover:border-acid/60 transition-colors group"
                >
                  <div>
                    <div className="text-ink text-sm font-medium">{p.name}</div>
                    <div className="text-eyebrow text-ink-dim mt-0.5">{p.role}</div>
                  </div>
                  <span
                    className={`text-[9px] uppercase font-mono tracking-[0.22em] px-2 py-1 ${
                      p.status === 'compliant' || p.status === 'aligned' ? 'text-acid border border-acid/40' : 'text-ink-muted border border-line'
                    }`}
                  >
                    {p.status === 'in_progress'
                      ? (locale === 'es' ? 'EN CURSO' : 'IN PROGRESS')
                      : p.status === 'aligned'
                      ? (locale === 'es' ? 'ALINEADO' : 'ALIGNED')
                      : (locale === 'es' ? 'CUMPLE' : 'COMPLIANT')}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Impact Report */}
        <div className="mt-16 md:mt-20 border-t border-line pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-ink text-base md:text-lg max-w-xl leading-relaxed">
            {t('cta_text', 'Cuando instales MODARIA en tu casa, cada mes recibes tu Informe de Impacto real con CO₂, agua, residuos y viajes ahorrados — auditable y descargable.')}
          </p>
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="impact-cta-report"
              className="btn-acid magnetic !text-[11px]"
            >
              <FileText size={13} /> {t('cta_primary', 'Hablar con MODARIA')}
            </a>
            <a
              href="https://www.carbonfact.com/lca-for-fashion"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost magnetic !text-[11px]"
              data-testid="impact-cta-method"
            >
              {t('cta_secondary', 'Ver metodología')} <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
